import React, { useState, useEffect } from 'react';
import { GameMode, GameState } from './types';
import { GameScreen } from './components/GameScreen';
import { ModeSelection } from './components/ModeSelection';
import { initGame } from './services/engine';
import { loadData } from './services/dataLoader';
import { TopBar } from './components/TopBar';
import { AuditPage } from './components/AuditPage';
import { PromptHelperPage } from './components/PromptHelperPage';
import { getCurrentRoute, navigate } from './router';

// Root app component
export default function App() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [activeView, setActiveView] = useState<'game' | 'audit' | 'prompt'>('game');
  const [statMode, setStatMode] = useState(false);
  const [hintMode, setHintMode] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = window.localStorage.getItem('theme');
    return stored === 'light' || stored === 'dark' ? stored : 'dark';
  });

  useEffect(() => {
    // Initialize data layer
    loadData().then(() => {
      setDataLoaded(true);
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('theme-light', theme === 'light');
    window.localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handlePopState = () => {
      const route = getCurrentRoute();
      if (route.type === 'audit') {
        setActiveView('audit');
        setGameState(null);
      } else if (route.type === 'prompt') {
        setActiveView('prompt');
        setGameState(null);
      } else if (route.type === 'game') {
        setActiveView('game');
        setGameState((prev) => {
          if (prev && prev.mode === route.mode && prev.status !== 'MENU') {
            return prev;
          }
          return initGame(route.mode);
        });
      } else {
        setActiveView('game');
        setGameState(null);
      }
    };

    handlePopState();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleStartGame = (mode: GameMode) => {
    const initialState = initGame(mode);
    setGameState(initialState);
    navigate({ type: 'game', mode });
  };

  const handleExit = () => {
    setGameState(null);
    navigate({ type: 'home' });
  };

  if (!dataLoaded) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm font-medium text-muted-foreground">Loading World Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased selection:bg-primary/30">
      <TopBar
        activeView={activeView}
        onChangeView={(view) => {
          setActiveView(view);
          if (view === 'audit') {
            navigate({ type: 'audit' });
            setGameState(null);
          } else if (view === 'prompt') {
            navigate({ type: 'prompt' });
            setGameState(null);
          } else {
            navigate({ type: 'home' });
          }
        }}
        theme={theme}
        onToggleTheme={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
        statMode={statMode}
        onToggleStatMode={() => setStatMode((prev) => !prev)}
        hintMode={hintMode}
        onToggleHintMode={() => setHintMode((prev) => !prev)}
      />
      <div className="min-h-[calc(100dvh-3.5rem)]">
        {activeView === 'audit' ? (
          <AuditPage />
        ) : activeView === 'prompt' ? (
          <PromptHelperPage />
        ) : !gameState || gameState.status === 'MENU' ? (
          <ModeSelection onSelectMode={handleStartGame} />
        ) : (
          <GameScreen
            initialState={gameState}
            onExit={handleExit}
            statMode={statMode}
            hintMode={hintMode}
          />
        )}
      </div>
    </div>
  );
}
